import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { uploadProfileImage, deleteProfileImage, exportHealthPDF } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PushNotificationSettings from '../components/PushNotifications';
import { NotificationSettings } from '../components/NotificationSettings';
import { toast } from 'sonner';
import {
    Settings as SettingsIcon, User, Globe,
    Camera, X, Dumbbell, Cake, Sparkles, Check
} from 'lucide-react';
import { WhatsNewSheet } from '../components/WhatsNewSheet';
import { HobbiesConditionsCards } from '../components/settings/HobbiesConditionsCards';
import { GoalsCard } from '../components/settings/GoalsCard';
import { ProfileBackupCard } from '../components/settings/ProfileBackupCard';
import { AutoFillCard } from '../components/settings/AutoFillCard';
import { DataExportCard } from '../components/settings/DataExportCard';
import { AppCacheCard } from '../components/settings/AppCacheCard';
import { WhatsNewCard } from '../components/settings/WhatsNewCard';
import { SmartPushCard } from '../components/settings/SmartPushCard';
import { AiStatusCard } from '../components/AiStatusCard';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
    const { user, updateProfile, logout } = useAuth();
    const [optimalCal, setOptimalCal] = useState(null);

    useEffect(() => {
        axios.get(`${API}/api/energy/optimal-calories`)
            .then(res => setOptimalCal(res.data))
            .catch(() => setOptimalCal(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
    }, []);
    const { language, setLanguage, t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [exportingPDF, setExportingPDF] = useState(false);
    const [autoFilling, setAutoFilling] = useState(false);
    const fileInputRef = useRef(null);
    
    const [profileForm, setProfileForm] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        name: user?.name || '',
        birth_date: user?.birth_date || '',
        gender: user?.gender || '',
        weight: user?.weight || '',
        height: user?.height || '',
        fitness_level: user?.fitness_level || 'beginner',
        hobbies: user?.hobbies || [],
        known_conditions: user?.known_conditions || [],
        calorie_goal: user?.calorie_goal || 2000,
        water_goal_ml: user?.water_goal_ml || 2500,
        sleep_goal_hours: user?.sleep_goal_hours || 8,
        protein_goal_g: user?.protein_goal_g || '',
        carbs_goal_g: user?.carbs_goal_g || '',
        fat_goal_g: user?.fat_goal_g || '',
        // Nutrition estimation preferences (v3.20)
        portion_size_pref: user?.portion_size_pref || 'medium',
        eating_style: user?.eating_style || 'balanced',
        activity_credit_mode: user?.activity_credit_mode || 'liberal',
        // Sleep defaults
        default_sleep_time: user?.default_sleep_time || '23:00',
        default_wake_time: user?.default_wake_time || '07:00',
        default_sleep_quality: user?.default_sleep_quality || 7,
        // Nanouk smart-comment style (v3.59 — Best Buddy default)
        smart_comment_style: user?.smart_comment_style || 'coach'
    });

    // CRITICAL: Re-sync form whenever the authenticated user object changes
    // (e.g., on page-load while auth is still fetching, or after re-login).
    // Without this, a stale form would overwrite the user's saved values on save.
    useEffect(() => {
        if (!user) return;
        setProfileForm(prev => ({
            ...prev,
            first_name: user.first_name ?? prev.first_name,
            last_name: user.last_name ?? prev.last_name,
            name: user.name ?? prev.name,
            birth_date: user.birth_date ?? prev.birth_date,
            gender: user.gender ?? prev.gender,
            weight: user.weight ?? prev.weight,
            height: user.height ?? prev.height,
            fitness_level: user.fitness_level ?? prev.fitness_level,
            hobbies: user.hobbies ?? prev.hobbies,
            known_conditions: user.known_conditions ?? prev.known_conditions,
            calorie_goal: user.calorie_goal ?? prev.calorie_goal,
            water_goal_ml: user.water_goal_ml ?? prev.water_goal_ml,
            sleep_goal_hours: user.sleep_goal_hours ?? prev.sleep_goal_hours,
            protein_goal_g: user.protein_goal_g ?? '',
            carbs_goal_g: user.carbs_goal_g ?? '',
            fat_goal_g: user.fat_goal_g ?? '',
            portion_size_pref: user.portion_size_pref ?? 'medium',
            eating_style: user.eating_style ?? 'balanced',
            taler_enabled: user.taler_enabled ?? prev.taler_enabled,
            taler_daily_budget: user.taler_daily_budget ?? prev.taler_daily_budget,
            activity_credit_mode: user.activity_credit_mode ?? 'liberal',
            smart_comment_style: user.smart_comment_style ?? 'coach',
            default_sleep_time: user.default_sleep_time ?? prev.default_sleep_time,
            default_wake_time: user.default_wake_time ?? prev.default_wake_time,
            default_sleep_quality: user.default_sleep_quality ?? prev.default_sleep_quality,
        }));
    }, [user]);
    
    const [showWhatsNew, setShowWhatsNew] = useState(false);

    const handleSave = async () => {
        // SAFETY: never save while auth is still loading — would overwrite user's data with form defaults
        if (!user) {
            toast.error(language === 'de' ? 'Profil lädt noch... bitte kurz warten.' : 'Profile still loading...');
            return;
        }
        setLoading(true);
        try {
            const fullName = `${profileForm.first_name} ${profileForm.last_name}`.trim() || profileForm.name;
            
            // Safely parse numbers, avoiding NaN
            const parseIntSafe = (val) => {
                const num = parseInt(val);
                return isNaN(num) ? null : num;
            };
            const parseFloatSafe = (val) => {
                const num = parseFloat(val);
                return isNaN(num) ? null : num;
            };
            
            await updateProfile({
                name: fullName,
                first_name: profileForm.first_name || null,
                last_name: profileForm.last_name || null,
                birth_date: profileForm.birth_date || null,
                gender: profileForm.gender || null,
                weight: parseFloatSafe(profileForm.weight),
                height: parseFloatSafe(profileForm.height),
                fitness_level: profileForm.fitness_level || null,
                hobbies: profileForm.hobbies?.length > 0 ? profileForm.hobbies : null,
                known_conditions: profileForm.known_conditions?.length > 0 ? profileForm.known_conditions : null,
                // Use saved user value as fallback so we never clobber with form defaults
                calorie_goal: parseIntSafe(profileForm.calorie_goal) || user?.calorie_goal || 2000,
                water_goal_ml: parseIntSafe(profileForm.water_goal_ml) || user?.water_goal_ml || 2500,
                sleep_goal_hours: parseFloatSafe(profileForm.sleep_goal_hours) || user?.sleep_goal_hours || 8,
                protein_goal_g: profileForm.protein_goal_g ? parseInt(profileForm.protein_goal_g) : null,
                carbs_goal_g: profileForm.carbs_goal_g ? parseInt(profileForm.carbs_goal_g) : null,
                fat_goal_g: profileForm.fat_goal_g ? parseInt(profileForm.fat_goal_g) : null,
                portion_size_pref: profileForm.portion_size_pref || 'medium',
                eating_style: profileForm.eating_style || 'balanced',
                taler_enabled: !!profileForm.taler_enabled,
                taler_daily_budget: parseIntSafe(profileForm.taler_daily_budget) || 18,
                activity_credit_mode: profileForm.activity_credit_mode || 'liberal',
                // Sleep defaults
                default_sleep_time: profileForm.default_sleep_time || null,
                default_wake_time: profileForm.default_wake_time || null,
                default_sleep_quality: parseIntSafe(profileForm.default_sleep_quality),
                // Nanouk persona / smart-comment tone
                smart_comment_style: profileForm.smart_comment_style || user?.smart_comment_style || 'coach'
            });
            toast.success(t('profile_updated'));
        } catch (error) {
            console.error('Save error:', error);
            console.error('Error details:', error.response?.data);
            const errorMsg = error.response?.data?.detail || t('error_saving');
            toast.error(typeof errorMsg === 'string' ? errorMsg : t('error_saving'));
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (file.size > 2 * 1024 * 1024) {
            toast.error(language === 'de' ? 'Bild zu groß (max 2MB)' : 'Image too large (max 2MB)');
            return;
        }
        
        setUploadingImage(true);
        try {
            const response = await uploadProfileImage(file);
            await updateProfile({ profile_image: response.data.profile_image });
            toast.success(language === 'de' ? 'Profilbild aktualisiert!' : 'Profile image updated!');
        } catch (error) {
            toast.error(language === 'de' ? 'Upload fehlgeschlagen' : 'Upload failed');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDeleteImage = async () => {
        try {
            await deleteProfileImage();
            await updateProfile({ profile_image: null });
            toast.success(language === 'de' ? 'Profilbild entfernt' : 'Profile image removed');
        } catch (error) {
            toast.error(t('error_deleting'));
        }
    };

    // ===== Profile Backup (Export / Import) =====
    const handleExportProfile = async () => {
        if (!user) {
            toast.error(language === 'de' ? 'Profil lädt noch' : 'Profile still loading');
            return;
        }
        try {
            const exportable = {
                _bestbuddy_backup_version: 1,
                _exported_at: new Date().toISOString(),
                first_name: user.first_name ?? null,
                last_name: user.last_name ?? null,
                birth_date: user.birth_date ?? null,
                gender: user.gender ?? null,
                weight: user.weight ?? null,
                height: user.height ?? null,
                fitness_level: user.fitness_level ?? null,
                hobbies: user.hobbies ?? [],
                known_conditions: user.known_conditions ?? [],
                medical_conditions: user.medical_conditions ?? [],
                allergies: user.allergies ?? [],
                calorie_goal: user.calorie_goal ?? null,
                water_goal_ml: user.water_goal_ml ?? null,
                sleep_goal_hours: user.sleep_goal_hours ?? null,
                protein_goal_g: user.protein_goal_g ?? null,
                carbs_goal_g: user.carbs_goal_g ?? null,
                fat_goal_g: user.fat_goal_g ?? null,
                default_sleep_time: user.default_sleep_time ?? null,
                default_wake_time: user.default_wake_time ?? null,
                default_sleep_quality: user.default_sleep_quality ?? null,
            };
            const blob = new Blob([JSON.stringify(exportable, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `bestbuddy-profile-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(language === 'de' ? '📁 Profil-Backup heruntergeladen' : '📁 Profile backup downloaded');
            // Mark backup so the weekly reminder push pauses
            try {
                await axios.post(`${API}/api/auth/profile/backup-noted`, {});
            } catch (err) { console.warn('backup-noted (non-critical):', err); }
        } catch (e) {
            console.error(e);
            toast.error(language === 'de' ? 'Export fehlgeschlagen' : 'Export failed');
        }
    };

    const handleImportProfileFile = async (file) => {
        if (!file) return;
        try {
            const text = await file.text();
            const data = JSON.parse(text);
            if (!data || typeof data !== 'object' || data._bestbuddy_backup_version !== 1) {
                toast.error(language === 'de' ? 'Ungültige Backup-Datei' : 'Invalid backup file');
                return;
            }
            // Confirm before overwriting
            const ok = window.confirm(
                language === 'de'
                    ? 'Profil-Backup importieren? Aktuelle Werte werden überschrieben.'
                    : 'Import profile backup? Current values will be overwritten.'
            );
            if (!ok) return;

            const allowedKeys = [
                'first_name','last_name','birth_date','gender','weight','height','fitness_level',
                'hobbies','known_conditions','medical_conditions','allergies',
                'calorie_goal','water_goal_ml','sleep_goal_hours',
                'protein_goal_g','carbs_goal_g','fat_goal_g',
                'portion_size_pref','eating_style','activity_credit_mode',
                'taler_enabled','taler_daily_budget',
                'default_sleep_time','default_wake_time','default_sleep_quality'
            ];
            const updates = {};
            for (const k of allowedKeys) {
                if (k in data) updates[k] = data[k];
            }
            await updateProfile(updates);
            toast.success(language === 'de' ? '✅ Profil-Backup importiert' : '✅ Profile backup imported');
        } catch (e) {
            console.error(e);
            toast.error(language === 'de' ? 'Import fehlgeschlagen' : 'Import failed');
        }
    };

    const calculateBMI = () => {
        if (profileForm.weight && profileForm.height) {
            const heightM = parseFloat(profileForm.height) / 100;
            const bmi = parseFloat(profileForm.weight) / (heightM * heightM);
            return bmi.toFixed(1);
        }
        return null;
    };

    const calculateAge = () => {
        if (profileForm.birth_date) {
            const birth = new Date(profileForm.birth_date);
            const today = new Date();
            let age = today.getFullYear() - birth.getFullYear();
            const m = today.getMonth() - birth.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
                age--;
            }
            return age;
        }
        return null;
    };

    const handleExportPDF = async (period = 'week') => {
        setExportingPDF(period);
        try {
            const response = await exportHealthPDF(language, period);
            // Create blob URL and trigger download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const userName = (user?.name || 'buddy').replace(/\s+/g, '_').toLowerCase();
            const periodLabel = period === 'month' ? 'monthly' : 'weekly';
            link.download = `bestbuddy_${periodLabel}_report_${userName}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success(language === 'de' ? 'PDF erfolgreich exportiert!' : 'PDF exported successfully!');
        } catch (error) {
            console.error('PDF export error:', error);
            toast.error(language === 'de' ? 'Export fehlgeschlagen' : 'Export failed');
        } finally {
            setExportingPDF(false);
        }
    };

    const profileImageUrl = user?.profile_image 
        ? (user.profile_image.startsWith('data:') ? user.profile_image : `${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`)
        : null;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="settings-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-6"
            >
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <SettingsIcon className="w-8 h-8 text-primary" />
                    <div>
                        <h1 className="font-display text-2xl lg:text-3xl font-bold">{t('settings_title')}</h1>
                        <p className="text-muted-foreground">{t('settings_subtitle')}</p>
                    </div>
                </div>

                {/* AI Status — surfaces budget / provider issues immediately */}
                <AiStatusCard />

                {/* Profile Image & Basic Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <User className="w-5 h-5" />
                            {language === 'de' ? 'Persönliche Daten' : 'Personal Information'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Profile Image */}
                        <div className="flex flex-col sm:flex-row items-center gap-6">
                            <div className="relative">
                                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center overflow-hidden border-4 border-background shadow-xl">
                                    {profileImageUrl ? (
                                        <img 
                                            src={profileImageUrl} 
                                            alt="Profile" 
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-4xl font-bold text-foreground">
                                            {(profileForm.first_name?.[0] || user?.name?.[0] || 'U').toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageUpload}
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    className="hidden"
                                />
                                <Button
                                    size="icon"
                                    className="absolute bottom-0 right-0 rounded-full h-9 w-9"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingImage}
                                >
                                    <Camera className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="text-center sm:text-left">
                                <p className="font-semibold text-lg">
                                    {profileForm.first_name || profileForm.last_name 
                                        ? `${profileForm.first_name} ${profileForm.last_name}`.trim()
                                        : user?.name || 'User'}
                                </p>
                                <p className="text-sm text-muted-foreground">{user?.email}</p>
                                {profileImageUrl && (
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="mt-2 text-destructive"
                                        onClick={handleDeleteImage}
                                    >
                                        <X className="w-4 h-4 mr-1" />
                                        {language === 'de' ? 'Bild entfernen' : 'Remove image'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Name Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="first_name">
                                    {language === 'de' ? 'Vorname' : 'First Name'}
                                </Label>
                                <Input
                                    id="first_name"
                                    value={profileForm.first_name}
                                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                                    placeholder={language === 'de' ? 'Max' : 'John'}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="last_name">
                                    {language === 'de' ? 'Nachname' : 'Last Name'}
                                </Label>
                                <Input
                                    id="last_name"
                                    value={profileForm.last_name}
                                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                                    placeholder={language === 'de' ? 'Mustermann' : 'Doe'}
                                />
                            </div>
                        </div>

                        {/* Birth Date & Gender */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="birth_date" className="flex items-center gap-2">
                                    <Cake className="w-4 h-4" />
                                    {language === 'de' ? 'Geburtsdatum' : 'Date of Birth'}
                                </Label>
                                <Input
                                    id="birth_date"
                                    type="date"
                                    value={profileForm.birth_date}
                                    onChange={(e) => setProfileForm({ ...profileForm, birth_date: e.target.value })}
                                />
                                {calculateAge() && (
                                    <p className="text-xs text-muted-foreground">
                                        {language === 'de' ? `${calculateAge()} Jahre alt` : `${calculateAge()} years old`}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="gender">{t('gender')}</Label>
                                <Select 
                                    value={profileForm.gender} 
                                    onValueChange={(value) => setProfileForm({ ...profileForm, gender: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={language === 'de' ? 'Auswählen' : 'Select'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="male">{t('male')}</SelectItem>
                                        <SelectItem value="female">{t('female')}</SelectItem>
                                        <SelectItem value="diverse">{t('diverse')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Weight & Height */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="weight">{t('weight_kg')}</Label>
                                <Input
                                    id="weight"
                                    type="number"
                                    value={profileForm.weight}
                                    onChange={(e) => setProfileForm({ ...profileForm, weight: e.target.value })}
                                    placeholder="70"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="height">{t('height_cm')}</Label>
                                <Input
                                    id="height"
                                    type="number"
                                    value={profileForm.height}
                                    onChange={(e) => setProfileForm({ ...profileForm, height: e.target.value })}
                                    placeholder="175"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('calculated_bmi')}</Label>
                                <div className="h-10 px-3 py-2 rounded-md border bg-muted/50 flex items-center">
                                    {calculateBMI() || '-'}
                                </div>
                            </div>
                        </div>

                        {/* Fitness Level */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Dumbbell className="w-4 h-4" />
                                {t('fitness_level')}
                            </Label>
                            <Select 
                                value={profileForm.fitness_level} 
                                onValueChange={(value) => setProfileForm({ ...profileForm, fitness_level: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="beginner">{t('beginner')}</SelectItem>
                                    <SelectItem value="intermediate">{t('intermediate')}</SelectItem>
                                    <SelectItem value="advanced">{t('advanced')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                <HobbiesConditionsCards t={t} language={language} profileForm={profileForm} setProfileForm={setProfileForm} />
                <GoalsCard t={t} language={language} profileForm={profileForm} setProfileForm={setProfileForm} handleSave={handleSave} loading={loading} optimalCal={optimalCal} />
                {/* Appearance Card entfernt — Light Mode ist jetzt Standard (Feb 2026) */}

                {/* Language Card */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Globe className="w-5 h-5" />
                            {t('language')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium">{t('language')}</p>
                                <p className="text-sm text-muted-foreground">{t('language_desc')}</p>
                            </div>
                            <Select value={language} onValueChange={setLanguage}>
                                <SelectTrigger className="w-40">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="de">
                                        <span className="flex items-center gap-2">🇩🇪 Deutsch</span>
                                    </SelectItem>
                                    <SelectItem value="en">
                                        <span className="flex items-center gap-2">🇬🇧 English</span>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Push Notifications */}
                <PushNotificationSettings />

                {/* Notification Preferences */}
                <NotificationSettings />

                {/* Nanouk Smart-Comment Style */}
                <Card data-testid="smart-comment-style-card">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Sparkles className="w-5 h-5 icon-primary" />
                            {language === 'de' ? 'Nanouks Notiz-Stil' : "Nanouk's note style"}
                        </CardTitle>
                        <CardDescription>
                            {language === 'de'
                                ? 'Wähle den Ton, in dem Nanouk dir Hinweise zu deinen Einträgen gibt.'
                                : 'Choose the tone for the notes Nanouk leaves on your entries.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {[
                            { id: 'science', emoji: '🔬', label_de: 'Wissenschaft', label_en: 'Science', sub_de: 'Präzise, evidenzbasiert (EPOC, Leucin, Zone 2)', sub_en: 'Precise, evidence-based (EPOC, leucine, Zone 2)' },
                            { id: 'coach', emoji: '👍', label_de: 'Coach', label_en: 'Coach', sub_de: 'Motivierend & klar — was tun und warum', sub_en: 'Motivating & clear — what to do and why' },
                            { id: 'mentor', emoji: '🧘', label_de: 'Mentor', label_en: 'Mentor', sub_de: 'Ruhig & langfristig — Muster statt Tagesform', sub_en: 'Calm & long-term — patterns not daily noise' },
                        ].map((opt) => {
                            const active = (profileForm.smart_comment_style || user?.smart_comment_style || 'coach') === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={async () => {
                                        // Optimistic local update + persist immediately so
                                        // the choice is saved even without hitting the global Save button.
                                        setProfileForm((f) => ({ ...f, smart_comment_style: opt.id }));
                                        try {
                                            await updateProfile({ smart_comment_style: opt.id });
                                            toast.success(language === 'de' ? 'Stil gespeichert' : 'Style saved');
                                        } catch {
                                            toast.error(language === 'de' ? 'Konnte Stil nicht speichern' : 'Could not save style');
                                        }
                                    }}
                                    data-testid={`smart-comment-style-${opt.id}`}
                                    className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                                        active
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border bg-card hover:bg-secondary'
                                    }`}
                                >
                                    <span className="text-2xl flex-shrink-0 leading-none mt-0.5">{opt.emoji}</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-foreground">{language === 'de' ? opt.label_de : opt.label_en}</p>
                                        <p className="text-xs text-muted-foreground">{language === 'de' ? opt.sub_de : opt.sub_en}</p>
                                    </div>
                                    {active && <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />}
                                </button>
                            );
                        })}
                    </CardContent>
                </Card>

                {/* Profile Backup — Export / Import */}
                <ProfileBackupCard
                    language={language}
                    user={user}
                    onExport={handleExportProfile}
                    onImportFile={handleImportProfileFile}
                />

                {/* Auto-Fill Data */}
                <AutoFillCard
                    language={language}
                    autoFilling={autoFilling}
                    setAutoFilling={setAutoFilling}
                />

                {/* Data Export */}
                <DataExportCard
                    language={language}
                    exportingPDF={exportingPDF}
                    onExportPDF={handleExportPDF}
                />

                {/* What's New trigger */}
                <WhatsNewCard language={language} onOpen={() => setShowWhatsNew(true)} />

                {/* Smart Insight Push toggle */}
                <SmartPushCard language={language} />

                {/* App Cache — Manual Reset */}
                <AppCacheCard language={language} />

                {/* Account Actions */}
                <Card className="border-destructive/20">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2 text-destructive">
                            {t('account')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium">{t('logout')}</p>
                                <p className="text-sm text-muted-foreground">{t('logout_desc')}</p>
                            </div>
                            <Button variant="outline" onClick={logout}>
                                {t('logout')}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Manually-triggered release notes (auto-shows once per version
                via Layout; here we let users re-open it anytime). */}
            <WhatsNewSheet open={showWhatsNew} onOpenChange={setShowWhatsNew} />
        </div>
    );
}
